import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { DESIGN_TOKENS } from '@/lib/design/theme';
import { cn } from '@/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import type { LucideIcon } from 'lucide-react-native';
import * as React from 'react';
import { Pressable, View, type PressableProps, type ViewProps, type ViewStyle } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

const cardVariants = cva('border', {
  variants: {
    tone: {
      default: '',
      featured: '',
      overlay: '',
      inset: '',
    },
  },
  defaultVariants: {
    tone: 'default',
  },
});

const chipVariants = cva('flex-row items-center gap-2 border px-4 py-2.5', {
  variants: {
    variant: {
      filter: '',
      status: '',
      segmented: '',
    },
    active: {
      true: '',
      false: '',
    },
  },
  defaultVariants: {
    variant: 'filter',
    active: false,
  },
});

export function AppScreen({
  children,
  className,
  contentClassName,
  immersive = false,
  edges = ['top'],
}: {
  children?: React.ReactNode;
  className?: string;
  contentClassName?: string;
  immersive?: boolean;
  edges?: React.ComponentProps<typeof SafeAreaView>['edges'];
}) {
  return (
    <SafeAreaView
      edges={edges}
      style={{ flex: 1, backgroundColor: DESIGN_TOKENS.colors.canvas, overflow: 'hidden' }}
      className={className}>
      <View className={cn('flex-1 overflow-hidden', contentClassName)}>
        <SceneBackground />
        <View className={cn('flex-1', immersive && 'bg-transparent')}>{children}</View>
      </View>
    </SafeAreaView>
  );
}

export function SceneBackground() {
  return (
    <View
      pointerEvents="none"
      className="absolute inset-0"
      style={{ backgroundColor: DESIGN_TOKENS.colors.canvas }}
    />
  );
}

export function Card({
  className,
  tone = 'default',
  style,
  ...props
}: ViewProps & VariantProps<typeof cardVariants>) {
  return (
    <View
      className={cn('overflow-hidden rounded-[26px] p-5', cardVariants({ tone }), className)}
      style={[getCardStyle(tone), style]}
      {...props}
    />
  );
}

export function ScreenHeader({
  eyebrow,
  title,
  subtitle,
  action,
  className,
  titleClassName,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  className?: string;
  titleClassName?: string;
}) {
  return (
    <View className={cn('flex-row items-start justify-between gap-4', className)}>
      <View className="flex-1 gap-2">
        {eyebrow ? (
          <Text variant="small" className="uppercase tracking-[2px] text-muted-foreground">
            {eyebrow}
          </Text>
        ) : null}
        <Text className={cn('text-4xl font-extrabold tracking-tight', titleClassName)}>
          {title}
        </Text>
        {subtitle ? (
          <Text variant="muted" className="max-w-[320px] leading-6">
            {subtitle}
          </Text>
        ) : null}
      </View>
      {action}
    </View>
  );
}

export function Chip({
  label,
  icon,
  active = false,
  onPress,
  className,
  variant = 'filter',
}: {
  label: string;
  icon?: LucideIcon;
  active?: boolean;
  onPress?: () => void;
  className?: string;
  variant?: 'filter' | 'status' | 'segmented';
}) {
  const textColor =
    variant === 'status'
      ? DESIGN_TOKENS.colors.text
      : active
        ? DESIGN_TOKENS.colors.text
        : DESIGN_TOKENS.colors.textMuted;
  const content = (
    <>
      {icon ? <Icon as={icon} size={14} color={textColor} /> : null}
      <Text
        variant="small"
        className={
          variant === 'status' || active
            ? 'font-semibold text-foreground'
            : 'text-muted-foreground'
        }>
        {label}
      </Text>
    </>
  );

  if (onPress) {
    return (
      <Pressable
        onPress={onPress}
        className={cn('rounded-full', chipVariants({ variant, active }), className)}
        style={getChipStyle({ variant, active })}>
        {content}
      </Pressable>
    );
  }

  return (
    <View
      className={cn('rounded-full', chipVariants({ variant, active }), className)}
      style={getChipStyle({ variant, active })}>
      {content}
    </View>
  );
}

export function StatBadge({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string;
  tone?: 'default' | 'warm';
}) {
  return (
    <View
      className="rounded-[20px] border px-4 py-3"
      style={
        tone === 'warm'
          ? {
              borderColor: DESIGN_TOKENS.colors.accentEdge,
              backgroundColor: DESIGN_TOKENS.colors.accentSoft,
            }
          : {
              borderColor: DESIGN_TOKENS.colors.border,
              backgroundColor: DESIGN_TOKENS.colors.surfaceRaised,
            }
      }>
      <Text variant="small" className="font-semibold text-foreground">
        {value}
      </Text>
      <Text variant="small" className="mt-1.5 text-muted-foreground">
        {label}
      </Text>
    </View>
  );
}

export function MetaBadge({
  label,
  tone = 'default',
}: {
  label: string;
  tone?: 'default' | 'warm';
}) {
  return (
    <View
      className="rounded-full border px-3 py-2"
      style={
        tone === 'warm'
          ? {
              borderColor: DESIGN_TOKENS.colors.accentEdge,
              backgroundColor: DESIGN_TOKENS.colors.accentSoft,
            }
          : {
              borderColor: DESIGN_TOKENS.colors.borderStrong,
              backgroundColor: DESIGN_TOKENS.colors.surfaceAlt,
            }
      }>
      <Text
        variant="small"
        className={tone === 'warm' ? 'font-semibold text-primary' : 'font-semibold text-foreground'}>
        {label}
      </Text>
    </View>
  );
}

export function IconButton({
  icon,
  onPress,
  className,
  variant = 'ghost',
  ...props
}: {
  icon: LucideIcon;
  className?: string;
  variant?: 'ghost' | 'filled';
} & Omit<PressableProps, 'children'>) {
  const filled = variant === 'filled';

  return (
    <Pressable
      onPress={onPress}
      className={cn('size-11 items-center justify-center rounded-full border', className)}
      style={{
        borderColor: filled ? DESIGN_TOKENS.colors.accentEdge : DESIGN_TOKENS.colors.border,
        backgroundColor: filled
          ? DESIGN_TOKENS.colors.accentSoft
          : DESIGN_TOKENS.colors.surfaceRaised,
      }}
      {...props}>
      <Icon
        as={icon}
        size={17}
        style={{
          color: filled ? DESIGN_TOKENS.colors.accent : DESIGN_TOKENS.colors.text,
        }}
      />
    </Pressable>
  );
}

export const SurfaceCard = Card;
export const SectionHeader = ScreenHeader;
export const GlassPill = Chip;
export const MetricPill = StatBadge;
export const IconAction = IconButton;

function getCardStyle(tone: VariantProps<typeof cardVariants>['tone']): ViewStyle {
  switch (tone) {
    case 'featured':
      return {
        backgroundColor: DESIGN_TOKENS.colors.surface,
        borderColor: DESIGN_TOKENS.colors.borderStrong,
        ...DESIGN_TOKENS.shadows.card,
      };
    case 'overlay':
      return {
        backgroundColor: DESIGN_TOKENS.colors.surfaceOverlay,
        borderColor: DESIGN_TOKENS.colors.borderStrong,
        ...DESIGN_TOKENS.shadows.overlay,
      };
    case 'inset':
      return {
        backgroundColor: DESIGN_TOKENS.colors.canvasInset,
        borderColor: DESIGN_TOKENS.colors.border,
      };
    default:
      return {
        backgroundColor: DESIGN_TOKENS.colors.surface,
        borderColor: DESIGN_TOKENS.colors.border,
        ...DESIGN_TOKENS.shadows.card,
      };
  }
}

function getChipStyle({
  variant,
  active,
}: {
  variant: 'filter' | 'status' | 'segmented';
  active: boolean;
}): ViewStyle {
  if (variant === 'status') {
    return {
      borderColor: active ? DESIGN_TOKENS.colors.accentEdge : DESIGN_TOKENS.colors.borderStrong,
      backgroundColor: active ? DESIGN_TOKENS.colors.accentSoft : DESIGN_TOKENS.colors.surfaceRaised,
    };
  }

  if (variant === 'segmented') {
    return {
      borderColor: active ? DESIGN_TOKENS.colors.borderStrong : 'transparent',
      backgroundColor: active ? DESIGN_TOKENS.colors.surfaceRaised : 'transparent',
    };
  }

  return {
    borderColor: active ? DESIGN_TOKENS.colors.borderStrong : DESIGN_TOKENS.colors.border,
    backgroundColor: active ? DESIGN_TOKENS.colors.surfaceRaised : DESIGN_TOKENS.colors.canvasInset,
  };
}
